// ConnectionsTab.js — convergence UI for the Connections alpha (ship plan D3; #99, #108).
//
// REDESIGN 2026-08-12 (owner + ChatGPT consult, "UI Design for Writer Tool"):
// the standalone Connections tab is gone. Convergence is a PROPERTY OF THE
// FINDING, not a third analytical mode — so the primary surface is the
// ConvergenceModule rendered INSIDE the axis spotlight, after the axis's own
// explanation and evidence (corroboration, not a feature badge), plus a ◇ mark
// on connected axis rows in the rail and a small "N shared findings" chip in
// the panel header that opens a drawer (discovery, not an IA tab).
//
// RENDERS ONLY WHAT pairingGate PASSES — unchanged. useConnections still
// re-runs the gate client-side; the presentation moved, the boundary did not.
// The four #108 writer actions survive unchanged: they attach to approved_key,
// not to a surface. "Show me on the page" is the one real action (it continues
// the analytical task: jump to the scene text with the quotation highlighted);
// helpful / not-helpful are quiet thumbs, hide lives behind the ⋯ menu.
//
// Copy rules still hold here: never DER, band, valence, or revision advice.
// The other instrument is named Craft Read, the axes side is Scene Mechanics.

import React, { useState, useEffect, useMemo } from 'react';
import { writerEvent, gatePairings } from './pairingGate';
import { tip } from './Tip';

// useConnections — the whole data path, so NodePanel's diff stays small.
// Server decides the allowlist ({enabled:false} means the feature does not
// exist); the client re-runs the gate so a tampered or stale payload still
// cannot render. Returns null until (and unless) there is something to show.
// Keyed by HASH — the front end is no longer given internal movie ids (2026-08-24).
export function useConnections(movieHash, sceneNumber) {
  const [pairings, setPairings] = useState(null);
  useEffect(() => {
    let live = true;
    setPairings(null);
    if (!movieHash || !sceneNumber) return () => { live = false; };
    fetch(`/sos2/connections/pairings?hash=${encodeURIComponent(movieHash)}&scene_number=${sceneNumber}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : { enabled: false, pairings: [] }))
      .then(async d => {
        if (!live || !d.enabled) return;
        const { renderable } = await gatePairings(d.pairings);   // defense in depth
        // Stamp the hash the payload was fetched with, so a writer event can name the script
        // without an internal id. Top-level on purpose: canonicalContent() only reads
        // scene/shared_moment/pairings/provenance, so this cannot disturb the approval key.
        renderable.forEach(p => { p.movie_hash = movieHash; });
        if (live && renderable.length) setPairings(renderable);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [movieHash, sceneNumber]);
  return pairings;
}

export function sendConnectionsEvent(ev) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  fetch('/sos2/connections/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { 'X-CSRF-TOKEN': token } : {}) },
    body: JSON.stringify(ev),
  }).catch(() => {});   // fire-and-forget; the log is an audit trail, not a UX dependency
}

// Group gated pairings by one side's key: key -> [{ pairing, links }].
// A pattern with two links on the same key yields ONE module with both
// counterparts inside it; a pattern spanning two keys appears under both
// (each side shows only its own links). Pure, so NodePanel can memo them.
function groupBy(pairings, field) {
  const out = {};
  for (const p of pairings || []) {
    const mine = {};
    for (const l of p.pairings || []) {
      (mine[l[field]] = mine[l[field]] || []).push(l);
    }
    for (const [k, links] of Object.entries(mine)) {
      (out[k] = out[k] || []).push({ pairing: p, links });
    }
  }
  return out;
}
export function groupByAxis(pairings) { return groupBy(pairings, 'scene_mechanics_axis'); }
export function groupByCriterion(pairings) { return groupBy(pairings, 'craft_read_criterion'); }

// Highlight helper for "Show me on the page": returns [before, <mark>, after]
// or null when the quotation cannot be located. Tries exact match first, then
// a whitespace-tolerant match (the scene text may reflow lines the cleaned
// quotation joined). Quotations stay verbatim — this only finds, never edits.
export function renderHighlighted(text, quote, markId) {
  if (!text || !quote) return null;
  let start = text.indexOf(quote);
  let end = start >= 0 ? start + quote.length : -1;
  if (start < 0) {
    const esc = quote.trim().split(/\s+/).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[\\s]+');
    try {
      const m = text.match(new RegExp(esc));
      if (m) { start = m.index; end = m.index + m[0].length; }
    } catch (e) { /* malformed pattern — no highlight */ }
  }
  if (start < 0) return null;
  return [
    text.slice(0, start),
    <mark key="hl" id={markId || 'conn-highlight'} style={{ background: '#fde68a', color: '#0f172a', padding: '0 .1em', borderRadius: 3 }}>{text.slice(start, end)}</mark>,
    text.slice(end),
  ];
}

const NAVY = '#1f3a5f';
const CRAFT = '#6d4c8f';   // Craft Read side gets its own quiet accent, distinct from the axis navy

export function criterionLabel(c) {
  return (c || '').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());
}

// One convergence module: this pattern, seen from ONE side's spotlight.
// side='mechanics' (default) renders in an axis spotlight and names the
// Craft Read criterion; side='craft' renders in a criterion spotlight and
// names the Scene Mechanics axis — the reciprocal jump. Order inside mirrors
// the pitch: other vocabulary -> page fact -> why they meet. onOpenOther
// (optional) makes the counterpart name a real link that switches views.
export function ConvergenceModule({ group, onEvent, onShowOnPage, hidden, onHide, side = 'mechanics', onOpenOther, otherLabel, linkable }) {
  const { pairing, links } = group;
  const [moreOpen, setMoreOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [voted, setVoted] = useState(null);   // 'helpful' | 'not_helpful' — session-local ack
  if (hidden) return null;
  const quotes = pairing.shared_moment.quotations || [];
  const primary = quotes[0];
  const rest = quotes.slice(1);
  const craftSide = side === 'craft';
  const otherName = craftSide ? 'Scene Mechanics' : 'Craft Read';
  const otherAccent = craftSide ? NAVY : CRAFT;
  const otherKey = (l) => (craftSide ? l.scene_mechanics_axis : l.craft_read_criterion);
  const labelOf = (k) => (otherLabel ? otherLabel(k) : criterionLabel(k));
  const act = (action) => {
    onEvent && onEvent(writerEvent(pairing, action));
    if (action === 'helpful' || action === 'not_helpful') setVoted(action);
    if (action === 'hide' && onHide) onHide(pairing.approval.approved_key);
  };
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #e6eef3' }}>
      {/* eyebrow — the corroboration claim, nothing more */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span {...tip('Another approved reading found the same page moment')}
          style={{ color: otherAccent, fontSize: '.7rem', fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase' }}>
          ◇ Also seen in {otherName}
        </span>
        <span style={{ marginLeft: 'auto', position: 'relative' }}>
          <button type="button" aria-label="More" onClick={() => setMenuOpen(o => !o)}
            style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '.9rem', padding: '0 .2rem' }}>⋯</button>
          {menuOpen && (
            <span style={{ position: 'absolute', right: 0, top: '1.3rem', zIndex: 5, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 14px rgba(15,23,42,.08)' }}>
              <button type="button" onClick={() => { setMenuOpen(false); act('hide'); }}
                style={{ display: 'block', background: 'transparent', border: 'none', padding: '.4rem .9rem', fontSize: '.82rem', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Hide this
              </button>
            </span>
          )}
        </span>
      </div>

      {/* the other vocabulary — only the NEW information (the reader already knows where they are).
          When the other view exists, the name is a real link: the reciprocal jump IS the education. */}
      <div style={{ marginTop: 6 }}>
        {links.map((l, i) => {
          const k = otherKey(l);
          const clickable = !!onOpenOther && (!linkable || linkable.has(k));
          return (
            <span key={i}>
              {i > 0 && <span style={{ color: '#94a3b8', fontWeight: 400 }}> · </span>}
              {clickable ? (
                <button type="button" onClick={() => onOpenOther(k)}
                  {...tip(`See what ${otherName} says here`)}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                    fontWeight: 700, color: otherAccent, fontSize: '.95rem' }}>
                  {labelOf(k)} ›
                </button>
              ) : (
                <span style={{ fontWeight: 700, color: otherAccent, fontSize: '.95rem' }}>{labelOf(k)}</span>
              )}
            </span>
          );
        })}
      </div>

      {/* mini-headline: what happened on the page, the human entry point */}
      <div style={{ marginTop: 4, color: '#0f172a', fontWeight: 600, fontSize: '.92rem', lineHeight: 1.4 }}>
        {pairing.shared_moment.label}
      </div>

      {/* one quotation gets visual priority; the rest wait behind a toggle */}
      {primary && (
        <blockquote style={{ borderLeft: `3px solid ${otherAccent}44`, margin: '8px 0 0', padding: '.15rem .6rem', fontSize: '.88rem', color: '#4a5a68', fontStyle: 'italic' }}>
          “{primary.text}”
        </blockquote>
      )}
      {rest.length > 0 && !moreOpen && (
        <button type="button" onClick={() => setMoreOpen(true)}
          style={{ background: 'transparent', border: 'none', padding: '2px 0 0', color: '#7b8a96', fontSize: '.78rem', cursor: 'pointer', textDecoration: 'underline' }}>
          + {rest.length} related excerpt{rest.length > 1 ? 's' : ''}
        </button>
      )}
      {moreOpen && rest.map((q, i) => (
        <blockquote key={i} style={{ borderLeft: '3px solid #e2e8f0', margin: '6px 0 0', padding: '.15rem .6rem', fontSize: '.85rem', color: '#64748b', fontStyle: 'italic' }}>
          “{q.text}”
        </blockquote>
      ))}

      {/* the payoff: why the two readings meet, its own sentence(s) */}
      <div style={{ marginTop: 8, color: '#475569', fontSize: '.88rem', lineHeight: 1.5 }}>
        {links.map((l, i) => <div key={i} style={{ marginTop: i ? 4 : 0 }}>{l.why}</div>)}
      </div>

      {/* one real action + quiet instrumentation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', marginTop: 10 }}>
        {onShowOnPage && primary && (
          <button type="button" onClick={() => { act('show_on_page'); onShowOnPage(primary.text); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f8fbfd', color: NAVY,
              border: `1px solid ${NAVY}2e`, borderRadius: 8, padding: '.3rem .7rem', fontSize: '.82rem', fontWeight: 600, cursor: 'pointer' }}>
            ⌖ Show me on the page
          </button>
        )}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '.35rem', alignItems: 'center' }}>
          <button type="button" aria-label="Helpful" {...tip('Helpful')} onClick={() => act('helpful')}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '.9rem', opacity: voted === 'helpful' ? 1 : 0.45 }}>👍</button>
          <button type="button" aria-label="Not helpful" {...tip('Not helpful')} onClick={() => act('not_helpful')}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '.9rem', opacity: voted === 'not_helpful' ? 1 : 0.45 }}>👎</button>
        </span>
      </div>
    </div>
  );
}

// The header chip + drawer: discovery, not an IA tab. "N shared findings"
// opens a quiet list of the scene's shared moments; picking one focuses the
// axis it lives on (the module renders there, in context).
export function SharedFindingsChip({ pairings, hiddenKeys, onPick }) {
  const [open, setOpen] = useState(false);
  const visible = useMemo(
    () => (pairings || []).filter(p => !hiddenKeys || !hiddenKeys.has(p.approval.approved_key)),
    [pairings, hiddenKeys],
  );
  if (!visible.length) return null;
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        {...tip('Moments here that two independent readings both landed on')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#faf8fd', color: CRAFT,
          border: `1px solid ${CRAFT}33`, borderRadius: 999, padding: '.18rem .65rem', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}>
        ◇ {visible.length} shared finding{visible.length > 1 ? 's' : ''}
      </button>
      {open && (
        <span style={{ position: 'absolute', left: 0, top: '1.8rem', zIndex: 6, minWidth: 300, maxWidth: 380, display: 'block',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 6px 20px rgba(15,23,42,.1)', padding: '.35rem' }}>
          {visible.map(p => {
            const axes = [...new Set((p.pairings || []).map(l => l.scene_mechanics_axis))];
            return (
              <button key={p.approval.approved_key} type="button"
                onClick={() => { setOpen(false); onPick && onPick(axes[0], p); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                  borderRadius: 7, padding: '.4rem .55rem', cursor: 'pointer', lineHeight: 1.35 }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f6f9fb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ display: 'block', color: '#0f172a', fontSize: '.85rem', fontWeight: 600 }}>{p.shared_moment.label}</span>
                <span style={{ display: 'block', color: '#94a3b8', fontSize: '.72rem', marginTop: 1 }}>
                  ◇ {axes.map(a => a.replace(/_/g, ' ')).join(' · ')}
                </span>
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}
